import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './channel-articles.module.scss';
import { WTypography, WSkeleton } from '../../components/wrappers/wrapper';
import dbService from '../../services/database.service';
import ArrowForwardIosOutlined from '@mui/icons-material/ArrowForwardIosOutlined';
import { TArticle, TChannel } from '../../models/common';
import useDocumentationArticles from 'custom-hooks/useDocumentationArticles';

const ChannelArticlesSkeletonItem = () => (
  <div className={styles.articleListItem}>
    <WSkeleton variant="text" width="80%" height={24} />
    <WSkeleton variant="circular" width={18} height={18} />
  </div>
);

const ChannelArticlesListItem = ({ article, onClick }: { article: TArticle; onClick: () => void }) => (
  <div className={styles.articleListItem} onClick={onClick}>
    <span className={styles.articleTitle}>{article.title}</span>
    <ArrowForwardIosOutlined fontSize="small" className={styles.articleArrowIcon} />
  </div>
);

export default function ChannelArticlesPage() {
  const { channelPath } = useParams();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState<TArticle[]>([]);
  const [channel, setChannel] = useState<TChannel | null>(null);
  const navigate = useNavigate();
  const { filterDocumentationArticles } = useDocumentationArticles();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [channelData] = await dbService.getChannels({ filter: { path: channelPath } });
        setChannel(channelData);
        const articlesData = await dbService.getArticles({
          filter: { 'channel._id': channelData._id },
          sort: { _id: 1 },
          relation: ['publisher', 'channel', 'channel.category'],
        });

        const isDocumentationChannel = (channelData.category as any)?.title === 'Documentation';
        const filteredArticles = filterDocumentationArticles(articlesData, isDocumentationChannel);

        setArticles(filteredArticles);
      } catch (e) {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [channelPath]);

  if (loading) {
    return (
      <div className={styles.container}>
        <WSkeleton variant="text" width={300} height={40} className={styles.title} />
        <div className={styles.articleList}>
          {Array.from({ length: 5 }).map((_, i) => (
            <ChannelArticlesSkeletonItem key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <WTypography variant="h4" className={styles.title}>{channel?.title || 'Articles'}</WTypography>
      <div className={styles.articleList}>
        {articles.length ? (
          articles.map((article) => (
            <ChannelArticlesListItem
              key={article._id}
              article={article}
              onClick={() => navigate(`/a/${article._id}`)}
            />
          ))
        ) : (
          <WTypography variant="body1" className={styles.noContent}>No articles found for this channel.</WTypography>
        )}
      </div>
    </div>
  );
} 